import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../App";
import { LogoMark } from "./Logo";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await register(name, email, password, businessName || null);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Left - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#0A0A0A] items-center justify-center p-12">
        <div className="max-w-[400px] text-center">
          <h2 className="font-display text-[36px] tracking-[-0.03em] text-white mb-4">
            Create invoices in<br />30 seconds.
          </h2>
          <p className="text-[16px] text-[#9CA3AF]">
            Join thousands of freelancers who stopped wasting time on invoicing.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#0A0A0A] mb-10 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2.5 mb-8">
            <LogoMark size={36} />
            <span className="font-bold text-[20px] tracking-[-0.02em] text-[#0A0A0A]">VeroBill</span>
          </div>

          <h1 className="font-display text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-2">
            Create your account
          </h1>
          <p className="text-[15px] text-[#6B7280] mb-8">
            Start invoicing in seconds. Free to get started.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[14px]" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Your name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="John Doe"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">
                Business name <span className="font-normal text-[#9CA3AF]">(optional)</span>
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="input"
                placeholder="Acme Studio"
                data-testid="register-business"
              />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              data-testid="register-submit"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-center text-[14px] text-[#6B7280]">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#1A3DFF] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
